import { useState } from 'react';
import {
  IComposableDashboardCollectionsContext,
  IComposableDashboardCollection,
  assignUnAssignToClients,
  assignUnAssignToClientSpoc,
  assignUnAssignToOrganisationalUnitAccountManagers,
  assignUnAssignToOrganisationalUnitTeams,
  assignUnAssignToTeams,
  assignUnAssignToAccountManagers,
  assignUnAssignToBusinessUnit,
  assignUnAssignToClientUnit,
  assignUnAssignToClientContactUnit,
  assignUnAssignToUsers,
  assignUnAssignToJobs,
  defaultComposableDashboardCollection,
} from 'wisestep-api';

import { success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import { useComposableDashboardCollectionsContext } from './useComposableDashboardCollectionsContext';

const useHandleAssignFlow = () => {
  const {
    dashboardCollections,
    setDashboardCollections,
    selectedDashboardCollection,
    setSelectedCollection,
  } = useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openAssignToClientsDialog, setOpenAssignToClientsDialog] = useState<boolean>(false);
  const [openAssignToClientSpocDialog, setOpenAssignToClientSpocDialog] = useState<boolean>(false);
  const [openAssignToAccountManagerOUDialog, setOpenAssignToAccountManagerOUDialog] =
    useState<boolean>(false);
  const [openAssignToTeamsOUDialog, setOpenAssignToTeamsOUDialog] = useState<boolean>(false);
  const [openAssignToTeamsDialog, setOpenAssignToTeamsDialog] = useState<boolean>(false);
  const [openAssignToAccountManagersDialog, setOpenAssignToAccountManagersDialog] =
    useState<boolean>(false);
  const [openAssignToBusinessDefinitionDialog, setOpenAssignToBusinessDefinitionDialog] =
    useState<boolean>(false);
  const [openAssignToClientUnitDialog, setOpenAssignToClientUnitDialog] = useState<boolean>(false);
  const [openAssignToClientContactUnitDialog, setOpenAssignToClientContactUnitDialog] =
    useState<boolean>(false);
  const [openAssignToUsersDialog, setOpenAssignToUsersDialog] = useState<boolean>(false);
  const [openAssignToJobsDialog, setOpenAssignToJobsDialog] = useState<boolean>(false);

  const getAccessLevel = (dashboardCollection: IComposableDashboardCollection) => {
    const isAssigned =
      dashboardCollection.assignToAccountManagerOU ||
      dashboardCollection.assignToClientSpoc ||
      dashboardCollection.assignToClients ||
      dashboardCollection.assignToTeamsOU ||
      dashboardCollection.assignToTeams ||
      dashboardCollection.assignToAccountManagers ||
      dashboardCollection.assignToBusinessUnit ||
      dashboardCollection.assignToClientUnit ||
      dashboardCollection.assignToClientContactUnit ||
      dashboardCollection.assignToUsers ||
      dashboardCollection.assignToJobs;

    return isAssigned ? 'public' : 'restricted';
  };

  const handleAssigningToClientsDashboard = async () => {
    setOpenAssignToClientsDialog(false);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        assignToClients: !selectedDashboardCollection?.assignToClients,
      };
      request.accessLevel = getAccessLevel(request);
      const response = await assignUnAssignToClients(request);
      setSelectedCollection(response.data);
      const id = response?.data?.id;
      const indexToUpdate = dashboardCollections.findIndex((collection) => collection.id == id);
      setDashboardCollections((prev) => {
        prev[indexToUpdate] = response.data;
        return [...prev];
      });
      selectedDashboardCollection?.assignToClients
        ? setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Unassigned from Clients`
            )
          )
        : setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Assigned to Clients`
            )
          );
    } catch (error) {
      setNotification(
        failed('Failed to Assign the dashboard collection to Clients. Please try again!')
      );
    }
  };

  const handleAssigningToClientSpocDashboard = async () => {
    setOpenAssignToClientSpocDialog(false);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        assignToClientSpoc: !selectedDashboardCollection?.assignToClientSpoc,
      };
      request.accessLevel = getAccessLevel(request);
      const response = await assignUnAssignToClientSpoc(request);
      setSelectedCollection(response.data);
      const id = response?.data?.id;
      const indexToUpdate = dashboardCollections.findIndex((collection) => collection.id == id);
      setDashboardCollections((prev) => {
        prev[indexToUpdate] = response.data;
        return [...prev];
      });
      selectedDashboardCollection?.assignToClientSpoc
        ? setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Unassigned from Client Spocs`
            )
          )
        : setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Assigned to Client Spocs`
            )
          );
    } catch (error) {
      setNotification(
        failed('Failed to Assign the dashboard collection to Client Spocs. Please try again!')
      );
    }
  };

  const handleAssigningToAccountManagerOUDashboard = async () => {
    setOpenAssignToAccountManagerOUDialog(false);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        assignToAccountManagerOU: !selectedDashboardCollection?.assignToAccountManagerOU,
      };
      request.accessLevel = getAccessLevel(request);
      const response = await assignUnAssignToOrganisationalUnitAccountManagers(request);
      setSelectedCollection(response.data);
      const id = response?.data?.id;
      const indexToUpdate = dashboardCollections.findIndex((collection) => collection.id == id);
      setDashboardCollections((prev) => {
        prev[indexToUpdate] = response.data;
        return [...prev];
      });
      selectedDashboardCollection?.assignToAccountManagerOU
        ? setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Unassigned from Account Units`
            )
          )
        : setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Assigned to Account Units`
            )
          );
    } catch (error) {
      setNotification(
        failed(
          'Failed to Assign the dashboard collection to Account Units. Please try again later!'
        )
      );
    }
  };

  const handleAssigningToTeamsOUDashboard = async () => {
    setOpenAssignToTeamsOUDialog(false);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        assignToTeamsOU: !selectedDashboardCollection?.assignToTeamsOU,
      };
      request.accessLevel = getAccessLevel(request);
      const response = await assignUnAssignToOrganisationalUnitTeams(request);
      setSelectedCollection(response.data);
      const id = response?.data?.id;
      const indexToUpdate = dashboardCollections.findIndex((collection) => collection.id == id);
      setDashboardCollections((prev) => {
        prev[indexToUpdate] = response.data;
        return [...prev];
      });
      selectedDashboardCollection?.assignToTeamsOU
        ? setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Unassigned from Delivery Units`
            )
          )
        : setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Assigned to Delivery Units`
            )
          );
    } catch (error) {
      setNotification(
        failed(
          'Failed to Assign the dashboard collection to Delivery Units. Please try again later!'
        )
      );
    }
  };

  const handleAssigningToTeamsDashboard = async () => {
    setOpenAssignToTeamsDialog(false);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        assignToTeams: !selectedDashboardCollection?.assignToTeams,
      };
      request.accessLevel = getAccessLevel(request);
      const response = await assignUnAssignToTeams(request);
      setSelectedCollection(response.data);
      const id = response?.data?.id;
      const indexToUpdate = dashboardCollections.findIndex((collection) => collection.id == id);
      setDashboardCollections((prev) => {
        prev[indexToUpdate] = response.data;
        return [...prev];
      });
      selectedDashboardCollection?.assignToTeams
        ? setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Unassigned from Teams`
            )
          )
        : setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Assigned to Teams`
            )
          );
    } catch (error) {
      setNotification(
        failed('Failed to Assign the dashboard collection to Teams. Please try again later!')
      );
    }
  };

  const handleAssigningToAccountManagersDashboard = async () => {
    setOpenAssignToAccountManagersDialog(false);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        assignToAccountManagers: !selectedDashboardCollection?.assignToAccountManagers,
      };
      request.accessLevel = getAccessLevel(request);
      const response = await assignUnAssignToAccountManagers(request);
      setSelectedCollection(response.data);
      const id = response?.data?.id;
      const indexToUpdate = dashboardCollections.findIndex((collection) => collection.id == id);
      setDashboardCollections((prev) => {
        prev[indexToUpdate] = response.data;
        return [...prev];
      });
      selectedDashboardCollection?.assignToAccountManagerOU
        ? setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Unassigned from Account Managers`
            )
          )
        : setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Assigned to Account Managers`
            )
          );
    } catch (error) {
      setNotification(
        failed(
          'Failed to assign the dashboard collection to Account Managers. Please try again later!'
        )
      );
    }
  };
  const handleAssigningToBusinessDefinitionDashboard = async () => {
    setOpenAssignToBusinessDefinitionDialog(false);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        assignToBusinessUnit: !selectedDashboardCollection?.assignToBusinessUnit,
      };
      request.accessLevel = getAccessLevel(request);
      const response = await assignUnAssignToBusinessUnit(request);
      setSelectedCollection(response.data);
      const id = response?.data?.id;
      const indexToUpdate = dashboardCollections.findIndex((collection) => collection.id == id);
      setDashboardCollections((prev) => {
        prev[indexToUpdate] = response.data;
        return [...prev];
      });
      selectedDashboardCollection?.assignToBusinessUnit
        ? setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Unassigned from Business Units`
            )
          )
        : setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Assigned to Business Units`
            )
          );
    } catch (error) {
      setNotification(
        failed(
          'Failed to Assign the dashboard collection to Business Units. Please try again later!'
        )
      );
    }
  };
  const handleAssigningToClientUnitDashboard = async () => {
    setOpenAssignToClientUnitDialog(false);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        assignToClientUnit: !selectedDashboardCollection?.assignToClientUnit,
      };
      request.accessLevel = getAccessLevel(request);
      const response = await assignUnAssignToClientUnit(request);
      setSelectedCollection(response.data);
      const id = response?.data?.id;
      const indexToUpdate = dashboardCollections.findIndex((collection) => collection.id == id);
      setDashboardCollections((prev) => {
        prev[indexToUpdate] = response.data;
        return [...prev];
      });
      selectedDashboardCollection?.assignToClientUnit
        ? setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Unassigned from Client Units`
            )
          )
        : setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Assigned to Client Units`
            )
          );
    } catch (error) {
      setNotification(
        failed('Failed to Assign the dashboard collection to Client Units. Please try again later!')
      );
    }
  };

  const handleAssigningToClientContactUnitDashboard = async () => {
    setOpenAssignToClientContactUnitDialog(false);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        assignToClientContactUnit: !selectedDashboardCollection?.assignToClientContactUnit,
      };
      request.accessLevel = getAccessLevel(request);
      const response = await assignUnAssignToClientContactUnit(request);
      setSelectedCollection(response.data);
      const id = response?.data?.id;
      const indexToUpdate = dashboardCollections.findIndex((collection) => collection.id == id);
      setDashboardCollections((prev) => {
        prev[indexToUpdate] = response.data;
        return [...prev];
      });
      selectedDashboardCollection?.assignToClientContactUnit
        ? setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Unassigned from Client Contact Units`
            )
          )
        : setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Assigned to Client Contact Units`
            )
          );
    } catch (error) {
      setNotification(
        failed(
          'Failed to Assign the dashboard collection to Business Units. Please try again later!'
        )
      );
    }
  };

  const handleAssigningToUsersDashboard = async () => {
    setOpenAssignToUsersDialog(false);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        assignToUsers: !selectedDashboardCollection?.assignToUsers,
      };
      request.accessLevel = getAccessLevel(request);
      const response = await assignUnAssignToUsers(request);
      setSelectedCollection(response.data);
      const id = response?.data?.id;
      const indexToUpdate = dashboardCollections.findIndex((collection) => collection.id == id);
      setDashboardCollections((prev) => {
        prev[indexToUpdate] = response.data;
        return [...prev];
      });
      selectedDashboardCollection?.assignToUsers
        ? setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Unassigned from Users`
            )
          )
        : setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Assigned to Users`
            )
          );
    } catch (error) {
      setNotification(
        failed('Failed to Assign the dashboard collection to Users. Please try again later!')
      );
    }
  };

  const handleAssigningToJobsDashboard = async () => {
    setOpenAssignToJobsDialog(false);
    try {
      const request: IComposableDashboardCollection = {
        ...selectedDashboardCollection,
        assignToJobs: !selectedDashboardCollection?.assignToJobs,
      };
      request.accessLevel = getAccessLevel(request);
      const response = await assignUnAssignToJobs(request);
      const data = response.data ?? defaultComposableDashboardCollection;
      setSelectedCollection(data);
      const id = response?.data?.id;
      const indexToUpdate = dashboardCollections.findIndex((collection) => collection.id == id);
      setDashboardCollections((prev) => {
        prev[indexToUpdate] = data;
        return [...prev];
      });
      selectedDashboardCollection?.assignToJobs
        ? setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Unassigned from Jobs`
            )
          )
        : setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been Assigned to Jobs`
            )
          );
    } catch (error) {
      setNotification(
        failed('Failed to Assign the dashboard collection to Users. Please try again later!')
      );
    }
  };

  return {
    openAssignToClientsDialog,
    openAssignToClientSpocDialog,
    openAssignToAccountManagerOUDialog,
    openAssignToTeamsOUDialog,
    openAssignToTeamsDialog,
    openAssignToAccountManagersDialog,
    openAssignToBusinessDefinitionDialog,
    openAssignToClientUnitDialog,
    openAssignToClientContactUnitDialog,
    openAssignToUsersDialog,
    openAssignToJobsDialog,
    setOpenAssignToClientsDialog,
    setOpenAssignToClientSpocDialog,
    setOpenAssignToAccountManagerOUDialog,
    setOpenAssignToTeamsOUDialog,
    setOpenAssignToTeamsDialog,
    setOpenAssignToAccountManagersDialog,
    setOpenAssignToBusinessDefinitionDialog,
    setOpenAssignToClientUnitDialog,
    setOpenAssignToClientContactUnitDialog,
    setOpenAssignToUsersDialog,
    setOpenAssignToJobsDialog,
    handleAssigningToClientsDashboard,
    handleAssigningToClientSpocDashboard,
    handleAssigningToAccountManagerOUDashboard,
    handleAssigningToTeamsOUDashboard,
    handleAssigningToTeamsDashboard,
    handleAssigningToAccountManagersDashboard,
    handleAssigningToBusinessDefinitionDashboard,
    handleAssigningToClientUnitDashboard,
    handleAssigningToClientContactUnitDashboard,
    handleAssigningToUsersDashboard,
    handleAssigningToJobsDashboard,
  };
};

export default useHandleAssignFlow;
